import { useState, useCallback, useRef, useEffect } from 'react';

// Audio format configuration for raw PCM from TTS backend
// s16le = signed 16-bit little-endian PCM (headerless)
const AUDIO_CONFIG = {
  sampleRate: 48000,     // Deepdub TTS sample rate
  channels: 1,           // Mono
  bytesPerSample: 2,     // 16-bit = 2 bytes
};

// Buffer configuration
const INITIAL_BUFFER_MS = 50; // Start playing after 50ms of audio
const INITIAL_BUFFER_SAMPLES = Math.floor(AUDIO_CONFIG.sampleRate * INITIAL_BUFFER_MS / 1000); // 2400 samples

// Adaptive prebuffering: when the network jitters and we drain mid-stream,
// bump the next start's prebuffer by 100ms each time, capped at 500ms.
const ADAPTIVE_BUFFER_STEP_MS = 100;
const ADAPTIVE_BUFFER_MAX_MS = 500;

// Soft-start gain ramp: when getUserMedia goes active, the OS switches the
// output device into "communication mode" and attenuates it — but the first
// audio plays before the transition completes, so it sounds loud and then
// settles. Ramp our master gain from this initial value to 1.0 over the
// duration below to mask the OS-level loudness step.
const SOFT_START_INITIAL_GAIN = 0.5;
const SOFT_START_RAMP_MS = 1000;

// On underrun the player can also ask the backend to halve the TTS sample
// rate (cheaper bytes on the wire). The rates form an integer-divisor chain
// of 48000 — the backend decimates by an integer factor right before send.
const SAMPLE_RATE_STEPDOWN: Record<number, number | undefined> = {
  48000: 24000,
  24000: 16000,
  16000: 8000,
};
const SAMPLE_RATE_REQUEST_COOLDOWN_MS = 3000;

function closeAudioContext(context: AudioContext | null) {
  if (!context || context.state === 'closed') return;
  void context.close().catch(() => {
    // Another lifecycle path may have closed it between the state check and close().
  });
}

export function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const softStartAppliedRef = useRef<boolean>(false);
  const nextStartTimeRef = useRef<number>(0);

  // Accumulated PCM samples buffer
  const accumulatedSamplesRef = useRef<Int16Array[]>([]);
  const totalAccumulatedSamplesRef = useRef<number>(0);
  const hasStartedPlaybackRef = useRef<boolean>(false);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  // Extra prebuffer ms applied to the next fresh playback start. Bumped on
  // each detected underrun, capped at ADAPTIVE_BUFFER_MAX_MS.
  const adaptiveExtraBufferMsRef = useRef<number>(0);
  // The sample rate the backend is currently sending. Defaults to 48k; the
  // backend can lower this on session start (or via session_config message)
  // to 24/16/8 kHz to reduce on-the-wire payload on slow links.
  const incomingSampleRateRef = useRef<number>(AUDIO_CONFIG.sampleRate);
  // Last sample of the previous chunk. Used as the left anchor for linear
  // interpolation when upsampling, so chunk boundaries are continuous —
  // without this, each upsampled buffer would re-anchor at zero and click.
  const lastUpsampleSampleRef = useRef<number>(0);
  // When false, the underrun-driven prebuffer bump is disabled.
  const adaptiveEnabledRef = useRef<boolean>(false);
  // Listeners told of every chunk scheduled: (startTime, duration) in the
  // context's seconds — when it really plays (onScheduled).
  const scheduledListenersRef = useRef(new Set<(startTime: number, duration: number) => void>());
  // When false, the underrun → request lower TTS rate from backend is
  // disabled. The auto-stepdown was deemed over-sensitive in production,
  // so this defaults to false and is opt-in via bot config.
  const autoQualityStepdownEnabledRef = useRef<boolean>(false);

  // setIncomingSampleRate is called by the consumer after receiving
  // session_config from the backend.
  const setIncomingSampleRate = useCallback((rate: number) => {
    if (rate <= 0) return;
    if (incomingSampleRateRef.current !== rate) {
      console.log(`🔊 TTS sample rate changed: ${incomingSampleRateRef.current} → ${rate} Hz`);
      incomingSampleRateRef.current = rate;
      // Reset the upsampler's left anchor — the previous sample lived in
      // the old rate's timebase and would interpolate poorly.
      lastUpsampleSampleRef.current = 0;
    }
  }, []);

  // upsampleLinear performs integer-factor upsampling with linear
  // interpolation, anchoring on the previous chunk's last sample so the
  // boundary between chunks is continuous. y[n*F + j] = x[n-1]*(1-j/F) +
  // x[n]*(j/F). Only past samples are needed (no lookahead → no latency).
  const upsampleLinear = useCallback((src: Int16Array, factor: number): Int16Array => {
    if (factor <= 1) return src;
    const out = new Int16Array(src.length * factor);
    let prev = lastUpsampleSampleRef.current;
    const invF = 1 / factor;
    for (let n = 0; n < src.length; n++) {
      const cur = src[n];
      for (let j = 0; j < factor; j++) {
        const t = j * invF;
        out[n * factor + j] = (prev * (1 - t) + cur * t) | 0;
      }
      prev = cur;
    }
    lastUpsampleSampleRef.current = prev;
    return out;
  }, []);

  const setAdaptivePrebufferEnabled = useCallback((enabled: boolean) => {
    adaptiveEnabledRef.current = enabled;
    if (!enabled) {
      adaptiveExtraBufferMsRef.current = 0;
    }
  }, []);

  const setAutoQualityStepdownEnabled = useCallback((enabled: boolean) => {
    autoQualityStepdownEnabledRef.current = enabled;
  }, []);

  // Callback the consumer wires to its WebSocket so the hook can send
  // `set_audio_rate` requests when it detects underruns.
  const requestSampleRateRef = useRef<((rate: number) => void) | null>(null);
  const setSampleRateRequester = useCallback((fn: ((rate: number) => void) | null) => {
    requestSampleRateRef.current = fn;
  }, []);
  const lastRateRequestAtRef = useRef<number>(0);

  // maybeRequestStepDown checks whether we should ask the backend for a
  // lower TTS rate. Called on each detected underrun. Only fires when the
  // bot config has auto_quality_stepdown_enabled = true; default is off
  // because the heuristic was over-sensitive in practice (a single
  // underrun could drop quality unnecessarily).
  const maybeRequestStepDown = useCallback(() => {
    if (!autoQualityStepdownEnabledRef.current) return;
    const send = requestSampleRateRef.current;
    if (!send) return;
    const cur = incomingSampleRateRef.current;
    const next = SAMPLE_RATE_STEPDOWN[cur];
    if (!next) return; // already at the floor
    const now = Date.now();
    if (now - lastRateRequestAtRef.current < SAMPLE_RATE_REQUEST_COOLDOWN_MS) return;
    lastRateRequestAtRef.current = now;
    console.log(`📉 Underrun: requesting TTS rate step-down ${cur} → ${next} Hz`);
    send(next);
  }, []);

  // Initialize audio context and analyser
  useEffect(() => {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
      sampleRate: AUDIO_CONFIG.sampleRate
    });
    audioContextRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    analyser.connect(gain);
    gain.connect(ctx.destination);
    analyserRef.current = analyser;
    masterGainRef.current = gain;
    console.log(`🔊 AudioContext initialized: sampleRate=${ctx.sampleRate}Hz`);
    
    return () => {
      const currentContext = audioContextRef.current;
      audioContextRef.current = null;
      analyserRef.current = null;
      masterGainRef.current = null;
      closeAudioContext(currentContext);
    };
  }, []);

  // Convert accumulated Int16 samples to Float32 and create AudioBuffer
  const createAudioBufferFromSamples = useCallback((samples: Int16Array[]): AudioBuffer | null => {
    const audioContext = audioContextRef.current;
    if (!audioContext || samples.length === 0) return null;

    // Calculate total length
    const totalLength = samples.reduce((acc, arr) => acc + arr.length, 0);
    if (totalLength === 0) return null;

    // Merge all samples into one Float32Array
    const float32Data = new Float32Array(totalLength);
    let offset = 0;
    
    for (const int16Data of samples) {
      for (let i = 0; i < int16Data.length; i++) {
        float32Data[offset + i] = int16Data[i] / 32768.0;
      }
      offset += int16Data.length;
    }

    // Create AudioBuffer at the AudioContext's native rate. Any incoming
    // lower-rate audio has already been linearly upsampled to 48k in
    // playAudio() with cross-chunk state, so the browser does no per-
    // buffer resampling here.
    const audioBuffer = audioContext.createBuffer(
      AUDIO_CONFIG.channels,
      totalLength,
      AUDIO_CONFIG.sampleRate,
    );
    audioBuffer.getChannelData(0).set(float32Data);

    return audioBuffer;
  }, []);

  // Play the accumulated buffer
  const playAccumulatedBuffer = useCallback(async (flush: boolean = false) => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    // Underrun detection: playback was active but the playhead caught up to
    // or passed our scheduled tail. Treat as a fresh start and bump the
    // prebuffer for the upcoming "request" by 100ms (capped at 500ms).
    const now = audioContext.currentTime;
    const isUnderrun =
      hasStartedPlaybackRef.current &&
      nextStartTimeRef.current > 0 &&
      nextStartTimeRef.current <= now;
    if (isUnderrun && !flush) {
      // Always ask the backend for a lower sample rate on underrun — this is
      // bandwidth-driven and shouldn't depend on the prebuffer toggle.
      maybeRequestStepDown();
      if (adaptiveEnabledRef.current) {
        const next = Math.min(
          ADAPTIVE_BUFFER_MAX_MS,
          adaptiveExtraBufferMsRef.current + ADAPTIVE_BUFFER_STEP_MS,
        );
        if (next !== adaptiveExtraBufferMsRef.current) {
          console.log(`⚠️ Underrun: bumping adaptive prebuffer to +${next}ms`);
          adaptiveExtraBufferMsRef.current = next;
        }
        hasStartedPlaybackRef.current = false;
      }
    }

    // Check if we have enough samples to play (or flush is requested).
    const extraSamples = Math.floor(
      (AUDIO_CONFIG.sampleRate * adaptiveExtraBufferMsRef.current) / 1000,
    );
    const samplesNeeded = hasStartedPlaybackRef.current
      ? 1
      : INITIAL_BUFFER_SAMPLES + extraSamples;
    if (!flush && totalAccumulatedSamplesRef.current < samplesNeeded) {
      console.log(`⏳ Buffering: ${totalAccumulatedSamplesRef.current}/${samplesNeeded} samples`);
      return;
    }

    // Nothing to play
    if (accumulatedSamplesRef.current.length === 0) {
      if (flush) {
        console.log('✅ Flush requested but no samples to play');
        setIsPlaying(false);
      }
      return;
    }

    // Ensure audio context is running
    if (audioContext.state === 'suspended') {
      console.log('▶️ Resuming suspended audio context');
      await audioContext.resume();
    }

    // Take all accumulated samples
    const samplesToPlay = accumulatedSamplesRef.current;
    accumulatedSamplesRef.current = [];
    totalAccumulatedSamplesRef.current = 0;

    // Create audio buffer
    const audioBuffer = createAudioBufferFromSamples(samplesToPlay);
    if (!audioBuffer) {
      console.log('⚠️ Failed to create audio buffer');
      return;
    }

    // console.log(`🎵 Playing buffer: ${audioBuffer.duration.toFixed(3)}s (${audioBuffer.length} samples), flush=${flush}`);

    // Create and configure source -- route through analyser for visualization
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    const dest = analyserRef.current || audioContext.destination;
    source.connect(dest);

    // Calculate start time
    const currentTime = audioContext.currentTime;
    let startTime: number;
    
    if (!hasStartedPlaybackRef.current || nextStartTimeRef.current <= currentTime) {
      // First buffer or we fell behind - start with small delay
      startTime = currentTime + 0.01; // 10ms delay for safety
      console.log(`🎬 Starting playback: currentTime=${currentTime.toFixed(3)}s, startTime=${startTime.toFixed(3)}s`);
      hasStartedPlaybackRef.current = true;
      // Soft-start: only on the very first playback of the call, ramp the
      // master gain up so the OS device-mode transition doesn't sound loud.
      const gainNode = masterGainRef.current;
      if (gainNode && !softStartAppliedRef.current) {
        softStartAppliedRef.current = true;
        const g = gainNode.gain;
        g.cancelScheduledValues(currentTime);
        g.setValueAtTime(SOFT_START_INITIAL_GAIN, startTime);
        g.linearRampToValueAtTime(1.0, startTime + SOFT_START_RAMP_MS / 1000);
      }
    } else {
      // Schedule seamlessly after previous buffer
      startTime = nextStartTimeRef.current;
      // console.log(`➡️ Continuing: currentTime=${currentTime.toFixed(3)}s, startTime=${startTime.toFixed(3)}s`);
    }

    // Update next start time
    nextStartTimeRef.current = startTime + audioBuffer.duration;

    // Track active source
    activeSourcesRef.current.push(source);
    
    source.onended = () => {
      // Remove from active sources
      const idx = activeSourcesRef.current.indexOf(source);
      if (idx > -1) activeSourcesRef.current.splice(idx, 1);
      
      // console.log(`🏁 Buffer ended, active sources: ${activeSourcesRef.current.length}`);
      
      // Update playing state
      if (activeSourcesRef.current.length === 0 && accumulatedSamplesRef.current.length === 0) {
        setIsPlaying(false);
      }
    };

    // Start playback
    source.start(startTime);
    setIsPlaying(true);
    scheduledListenersRef.current.forEach((cb) => {
      try {
        cb(startTime, audioBuffer.duration);
      } catch (err) {
        console.error('Audio schedule listener failed:', err);
      }
    });
    
    // console.log(`▶️ Scheduled: start=${startTime.toFixed(3)}s, end=${nextStartTimeRef.current.toFixed(3)}s, duration=${audioBuffer.duration.toFixed(3)}s`);
  }, [createAudioBufferFromSamples]);

  // Main function to receive audio data
  const playAudio = useCallback((base64Audio: string, isFinal: boolean = false) => {
    try {
      // Decode base64 to bytes
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert to Int16Array
      let int16Data = new Int16Array(bytes.buffer);

      // Upsample to the AudioContext's native rate with cross-chunk state.
      // This avoids the browser's per-AudioBuffer resampler, whose lack of
      // lookahead at the buffer boundary causes periodic clicks at the
      // chunk cadence — most audible at 8 kHz (factor 6).
      const rateIn = incomingSampleRateRef.current;
      if (rateIn > 0 && rateIn < AUDIO_CONFIG.sampleRate) {
        const factor = AUDIO_CONFIG.sampleRate / rateIn;
        if (Number.isInteger(factor) && factor > 1) {
          int16Data = upsampleLinear(int16Data, factor);
        }
      }

      // Accumulate samples
      accumulatedSamplesRef.current.push(int16Data);
      totalAccumulatedSamplesRef.current += int16Data.length;

      // Try to play (will check if we have enough buffered)
      playAccumulatedBuffer(isFinal);

    } catch (err) {
      console.error('Error processing audio:', err);
    }
  }, [playAccumulatedBuffer, upsampleLinear]);

  // Flush any remaining buffered audio
  const flushAudio = useCallback(() => {
    console.log('🔄 Flushing remaining audio buffer');
    playAccumulatedBuffer(true);
  }, [playAccumulatedBuffer]);

  const stopAudio = useCallback(() => {
    console.log('🛑 Stopping audio playback');
    
    // Stop all active sources
    activeSourcesRef.current.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // Already stopped
      }
    });
    activeSourcesRef.current = [];
    
    // Clear accumulated samples
    accumulatedSamplesRef.current = [];
    totalAccumulatedSamplesRef.current = 0;
    hasStartedPlaybackRef.current = false;
    nextStartTimeRef.current = 0;
    adaptiveExtraBufferMsRef.current = 0;
    lastUpsampleSampleRef.current = 0;
    setIsPlaying(false);
    
    // Recreate audio context and analyser to ensure clean state
    if (audioContextRef.current) {
      closeAudioContext(audioContextRef.current);
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: AUDIO_CONFIG.sampleRate
      });
      audioContextRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      const gain = ctx.createGain();
      gain.gain.value = 1.0;
      analyser.connect(gain);
      gain.connect(ctx.destination);
      analyserRef.current = analyser;
      masterGainRef.current = gain;
    }
  }, []);

  // The node the speakers get (after the master gain's soft start), with its
  // context: a recorder taps it for exactly what was played. Both change
  // with every stopAudio (a new context).
  const getOutput = useCallback((): { context: AudioContext; node: AudioNode } | null => {
    const context = audioContextRef.current;
    const node = masterGainRef.current;
    return context && node && context.state !== 'closed' ? { context, node } : null;
  }, []);

  // Subscribe to every scheduled chunk: cb(startTime, duration), context seconds.
  const onScheduled = useCallback((cb: (startTime: number, duration: number) => void) => {
    scheduledListenersRef.current.add(cb);
    return () => {
      scheduledListenersRef.current.delete(cb);
    };
  }, []);

  // When the last scheduled audio ends (context seconds; 0: nothing scheduled).
  const getPlaybackEnd = useCallback(() => nextStartTimeRef.current, []);

  return {
    playAudio,
    flushAudio,
    stopAudio,
    isPlaying,
    getOutput,
    onScheduled,
    getPlaybackEnd,
    analyser: analyserRef.current,
    setIncomingSampleRate,
    setAdaptivePrebufferEnabled,
    setAutoQualityStepdownEnabled,
    setSampleRateRequester,
  };
}
